/**
 * API COVERAGE — HotelsController (/api/hotels): Module 3 Hotels, part 1 — list (H1), Basics (H2), Rooms (H3, H4),
 * the vendor gallery (H5), activate / deactivate, blacklist, delete, sample file, import and export.
 */
import { api, http } from '@/lib/apiClient';
import type { BulkResult, NamedRef, Skipped } from './places.api';
import type { PoiImportCheck } from './pois.api';
import type { ImportApi } from './subtours.api';
import type { VendorAddress } from './vendors.api';

export type HotelStatus = 'Draft' | 'PendingApproval' | 'Active' | 'Inactive' | 'Blacklisted' | 'ContractExpired';

export const HOTEL_STATUS_LABEL: Record<HotelStatus, string> = {
  Draft: 'Draft', PendingApproval: 'Pending approval', Active: 'Active', Inactive: 'Inactive', Blacklisted: 'Blacklisted', ContractExpired: 'Contract expired',
};

export interface HotelListItem {
  id: number; code: string; name: string; destinationId: number; destinationName: string; countryName?: string | null; stateName?: string | null;
  category?: string | null; starRating?: number | null; contractsFrom: string; ratesValidTill?: string | null; status: HotelStatus;
  vendorId: number; vendorCode: string; vendorName: string;
}
export interface HotelList { items: HotelListItem[]; total: number; all: number; expiringSoon: number }
export interface HotelListParams {
  search?: string; country?: string; state?: string; destinationId?: number; category?: string; status?: string; page: number; pageSize: number;
}
export interface HotelFilters { countries: { iso2: string; name: string }[]; states: string[] }

export interface HotelContact { id?: number | null; department: string; name: string; phone?: string | null; email?: string | null }
export interface HotelRoom {
  id: number; name: string; bedType?: string | null; size?: string | null; view?: string | null; maxAdults: number; maxChildren: number; maxTotal: number;
  roomCount: number; description?: string | null; descriptionOriginal?: string | null; imageIds: number[]; hasRates: boolean;
}
export interface HotelRoomIn {
  id?: number | null; name: string; bedType?: string | null; size?: string | null; view?: string | null; maxAdults: number; maxChildren: number;
  maxTotal: number; roomCount: number; description?: string | null; keepDescriptionAsTyped: boolean; imageIds: number[];
}
export interface Hotel {
  id: number; code: string; name: string; destinationId: number; destinationCode: string; destinationName: string; vendorId: number; vendorCode: string;
  vendorName: string; vendorStatus: string; category?: string | null; starRating?: number | null; typeTags: string[]; checkInTime?: string | null;
  checkOutTime?: string | null; shortDescription?: string | null; shortDescriptionOriginal?: string | null; longDescription?: string | null;
  longDescriptionOriginal?: string | null; address: VendorAddress; amenityIds: number[]; floors?: number | null; hasLift?: boolean | null; totalRooms: number;
  status: HotelStatus; blacklistReason?: string | null; contacts: HotelContact[]; rooms: HotelRoom[]; contracts: number; coverFileId?: number | null;
  source: string; missingForActive: string[];
}
export interface SaveHotel {
  name: string; destinationId: number; vendorId: number | null; category?: string | null; starRating?: number | null; typeTags: string[];
  checkInTime?: string | null; checkOutTime?: string | null; shortDescription?: string | null; longDescription?: string | null; keepShortAsTyped: boolean;
  keepLongAsTyped: boolean; address: VendorAddress; amenityIds: number[]; floors?: number | null; hasLift?: boolean | null; contacts: HotelContact[];
  rooms: HotelRoomIn[];
}
export type ImageUse = 'Cover' | 'Hotel' | 'Room';
export interface GalleryImage { id: number; fileId: number; title?: string | null; use?: ImageUse | null; hotelId?: number | null; roomCategoryId?: number | null; usedFor: string; sortOrder: number; width?: number | null; height?: number | null; sizeNote?: string | null }

async function download(url: string, body: unknown | undefined, fallback: string) {
  const res = body === undefined ? await http.get(url, { responseType: 'blob' }) : await http.post(url, body, { responseType: 'blob' });
  const name = /filename="?([^";]+)"?/i.exec(res.headers['content-disposition'] ?? '')?.[1] ?? fallback;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href; a.download = name; a.click();
  URL.revokeObjectURL(href);
}

export const HOTELS_KEY = 'hotels';
const base = '/hotels';
export const hotelsApi = {
  list: (p: HotelListParams) => api.get<HotelList>(base, p as unknown as Record<string, unknown>),
  filters: () => api.get<HotelFilters>(`${base}/filters`),
  options: (p: { destinationId?: number; search?: string } = {}) => api.get<NamedRef[]>(`${base}/options`, p),
  get: (id: number) => api.get<Hotel>(`${base}/${id}`),
  create: (b: SaveHotel) => api.post<Hotel>(base, b),
  update: (id: number, b: SaveHotel) => api.put<Hotel>(`${base}/${id}`, b),
  setStatus: (ids: number[], status: 'Active' | 'Inactive') => api.post<BulkResult>(`${base}/status`, { ids, status }),
  blacklist: (ids: number[], reason: string) => api.post<BulkResult>(`${base}/blacklist`, { ids, reason }),
  reactivate: (ids: number[]) => api.post<BulkResult>(`${base}/reactivate`, { ids }),
  deletePreview: (ids: number[]) => api.post<{ willDelete: NamedRef[]; willSkip: Skipped[] }>(`${base}/delete-preview`, { ids }),
  remove: (ids: number[]) => api.post<BulkResult>(`${base}/delete`, { ids }),
  exportExcel: (body: { ids?: number[] } & Partial<HotelListParams>) => download(`${base}/export`, body, 'hotels.xlsx'),
  gallery: (id: number) => api.get<GalleryImage[]>(`${base}/${id}/gallery`),
  addImages: (id: number, fileIds: number[], sizes?: { width: number; height: number }[]) => api.post<GalleryImage[]>(`${base}/${id}/gallery`, { fileIds, sizes }),
  updateImage: (id: number, imageId: number, b: { title?: string | null; use?: ImageUse | null; roomCategoryId?: number | null }) =>
    api.put<GalleryImage[]>(`${base}/${id}/gallery/${imageId}`, b),
  reorder: (id: number, imageIds: number[]) => api.post<GalleryImage[]>(`${base}/${id}/gallery/order`, { imageIds }),
  removeImage: (id: number, imageId: number) => api.del<GalleryImage[]>(`${base}/${id}/gallery/${imageId}`),
};

export const hotelImportApi: ImportApi = {
  sample: () => download(`${base}/sample`, undefined, 'hotels_sample.xlsx'),
  checkImport: (file: File) => { const f = new FormData(); f.append('file', file); return api.post<PoiImportCheck>(`${base}/import/check`, f); },
  importRows: (fileName, rows, skipErrorRows) =>
    api.post<{ batchId: number; imported: number; skipped: number; message: string }>(`${base}/import`, { fileName, rows, skipErrorRows }),
  undoImport: (batchId) => api.post<string>(`${base}/import/${batchId}/undo`),
};

// ---------------------------------------------------------------------------------------------------------------
// Part 2 — contracts (H6–H11), the rate calculator (H13) and the GST slabs (kept on the platform).
// ---------------------------------------------------------------------------------------------------------------

export type ContractStatus = 'Draft' | 'PendingApproval' | 'Active' | 'Rejected' | 'Expired';
export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = {
  Draft: 'Draft', PendingApproval: 'Pending approval', Active: 'Active', Rejected: 'Sent back', Expired: 'Expired',
};
export type Market = 'Indian' | 'Foreign' | 'B2B';
export const MARKETS: Market[] = ['Indian', 'Foreign', 'B2B'];
export const MARKET_LABEL: Record<Market, string> = { Indian: 'Indian', Foreign: 'Foreign', B2B: 'B2B / agent' };
export type RateBasis = 'Net' | 'Commissionable';
export type CommissionType = 'Pct' | 'Flat';
export type DayType = 'All' | 'Weekend';
export type ChildCharge = 'Free' | 'ChildRate' | 'ExtraAdult';
export const CHILD_CHARGE_LABEL: Record<ChildCharge, string> = { Free: 'Free, no bed', ChildRate: 'Child with bed or child no bed rate', ExtraAdult: 'Extra adult rate' };
export type SupplementBasis = 'PerPerson' | 'PerAdult' | 'PerCouple' | 'PerRoom' | 'PerRoomNight' | 'PerStay' | 'PctOfNight';
export const SUPPLEMENT_BASIS_LABEL: Record<SupplementBasis, string> = {
  PerPerson: 'per adult · per child', PerAdult: 'per adult', PerCouple: 'per couple', PerRoom: 'per room', PerRoomNight: 'per room night', PerStay: 'per stay',
  PctOfNight: '% of one night',
};
export type PromotionType = 'StayPay' | 'EarlyBird';

export interface DateRange { from: string; to: string }
export interface ContractRow {
  id: number; code: string; name: string; boughtFromVendorId: number; boughtFromCode: string; boughtFromName: string; direct: boolean; validFrom: string;
  validTo: string; markets: Market[]; rateBasis: RateBasis; commissionType?: CommissionType | null; commissionValue?: number | null; isPreferred: boolean;
  status: ContractStatus; source: string;
}
export interface Season { id?: number | null; key: string; name: string; ranges: DateRange[]; isFestival: boolean; minStay?: number | null }
export interface Rate {
  seasonKey: string; market: Market; roomCategoryId: number; mealPlan: string; dayType: DayType; single?: number | null; double?: number | null;
  extraAdult?: number | null; childWithBed?: number | null; childNoBed?: number | null;
}
export interface ChildBand { id?: number | null; band: 'Infant' | 'Child' | 'Adult'; ageFrom: number; ageTo?: number | null; chargedAs: ChildCharge }
export interface Supplement {
  id?: number | null; name: string; description?: string | null; isCompulsory: boolean; dateFrom?: string | null; dateTo?: string | null; basis: SupplementBasis;
  amount: number; childAmount?: number | null;
}
export interface Promotion {
  id?: number | null; name: string; type: PromotionType; stayNights?: number | null; payNights?: number | null; daysBefore?: number | null;
  discountPct?: number | null; validFrom?: string | null; validTo?: string | null;
}
export interface CancelSlab { daysFrom: number; daysTo?: number | null; chargePct: number; isNoShow: boolean }
export interface ContractRoom { id: number; name: string; maxAdults: number; maxChildren: number; maxTotal: number }
export interface Contract {
  id: number; code: string; name: string; hotelId: number; hotelCode: string; hotelName: string; hotelVendorId: number; boughtFromVendorId: number;
  boughtFromCode: string; boughtFromName: string; direct: boolean; validFrom: string; validTo: string; markets: Market[]; ratesIncludeGst?: boolean | null;
  rateBasis: RateBasis; commissionType?: CommissionType | null; commissionValue?: number | null; isPreferred: boolean; festivalOverrides: boolean;
  weekendDays: number[]; minStay?: number | null; status: ContractStatus; source: string; reviewNote?: string | null; copiedFromCode?: string | null;
  submittedAt?: string | null; approvedAt?: string | null; seasons: Season[]; rates: Rate[]; childBands: ChildBand[]; supplements: Supplement[];
  promotions: Promotion[]; slabs: CancelSlab[]; paymentTerms?: string | null; rooms: ContractRoom[]; gaps: DateRange[]; missingForApproval: string[];
  aiOpen?: number; sourceFileId?: number | null; submittedByVendorId?: number | null; submittedByName?: string | null;
}
export interface SaveContract {
  hotelId: number; boughtFromVendorId: number; validFrom: string; validTo: string; markets: Market[]; ratesIncludeGst?: boolean | null; rateBasis: RateBasis;
  commissionType?: CommissionType | null; commissionValue?: number | null; isPreferred: boolean; festivalOverrides: boolean; weekendDays: number[];
  minStay?: number | null; seasons: Season[]; rates: Rate[]; childBands: ChildBand[]; supplements: Supplement[]; promotions: Promotion[]; slabs: CancelSlab[];
  sendForApproval: boolean;
}
export interface ContractSource { id: number; code: string; name: string; direct: boolean; paymentTerms?: string | null }
export interface CopyContract { validFrom: string; validTo: string; upliftPct: number; roundTo: number; moveDatesByYear: boolean; copyExtras: boolean }
export interface CopyResult { id: number; code: string; message: string; flags: string[] }

export interface CalcChild { age: number; noBed?: boolean }
export interface CalcRoom { adults: number; children: CalcChild[] }
export interface CalcQuery {
  hotelId: number; roomCategoryId: number; checkIn: string; checkOut: string; mealPlan: string; market: Market; rooms: CalcRoom[]; contractId?: number | null;
}
export interface CalcNight { night: string; season: string; charge: number; ourCost: number; gstRate: number; gst: number; free: boolean; note?: string | null }
export interface CalcRoomResult {
  number: number; adults: number; children: number; infants: number; guests: string; overOccupancy: boolean; occupancyProblem?: string | null;
  nightsCharged: number; freeNights: number; perNight: string; gstRate: number; gst: number; roomCost: number;
  supplements: { name: string; basis: string; amount: number }[]; total: number; nights: CalcNight[];
}
export interface CalcContract {
  id: number; code: string; boughtFrom: string; direct: boolean; isPreferred: boolean; rateBasis: RateBasis; label: string; total?: number | null; problem?: string | null;
}
export interface CalcResult {
  contract?: CalcContract | null; nights: number; rooms: CalcRoomResult[]; gst: number; total: number; notes: string[]; problems: string[]; others: CalcContract[];
}
export interface GstSlab {
  id: number; countryIso2: string; fromValue: number; toValue?: number | null; rate: number; validFrom: string; validTo?: string | null;
  verifiedBy?: string | null; verifiedAt?: string | null; note?: string | null;
}

export const CONTRACTS_KEY = 'hotel-contracts';
export const contractsApi = {
  list: (hotelId: number) => api.get<ContractRow[]>(`${base}/${hotelId}/contracts`),
  sources: (hotelId: number) => api.get<ContractSource[]>(`${base}/${hotelId}/contract-sources`),
  get: (id: number) => api.get<Contract>(`${base}/contracts/${id}`),
  create: (hotelId: number, b: SaveContract) => api.post<Contract>(`${base}/${hotelId}/contracts`, b),
  update: (id: number, b: SaveContract) => api.put<Contract>(`${base}/contracts/${id}`, b),
  send: (id: number) => api.post<Contract>(`${base}/contracts/${id}/send`),
  approve: (id: number) => api.post<Contract>(`${base}/contracts/${id}/approve`),
  reject: (id: number, reason: string) => api.post<Contract>(`${base}/contracts/${id}/reject`, { reason }),
  prefer: (id: number) => api.post<string>(`${base}/contracts/${id}/preferred`),
  remove: (id: number) => api.del<string>(`${base}/contracts/${id}`),
  copy: (id: number, b: CopyContract) => api.post<CopyResult>(`${base}/contracts/${id}/copy`, b),
  calculate: (q: CalcQuery) => api.post<CalcResult>(`${base}/calculator`, q),
  gstSlabs: (country = 'IN') => api.get<GstSlab[]>(`${base}/gst-slabs`, { country }),
};

// ---------------------------------------------------------------------------------------------------------------
// Part 3 — Read rate sheet with AI (H12), the Approvals centre and the step-by-step review (H15).
// ---------------------------------------------------------------------------------------------------------------

export type AiCheck = 'Sure' | 'Check' | 'NotFound';
export interface AiItem { id: number; field: string; value?: string | null; pageRef?: string | null; sourceText?: string | null; check: AiCheck; resolved: boolean; answer?: string | null }
export interface RateSheet {
  contractId: number; contractCode: string; contractStatus: ContractStatus; hotelId: number; hotelName: string; fileId: number; fileName: string;
  contentType: string; pages: number; lines: string[]; items: AiItem[]; open: number;
}
export interface RateSheetRead { contractId: number; code: string; items: number; open: number; message: string }
export const GST_FIELD = 'Rates include GST?';

export const rateSheetApi = {
  read: (hotelId: number, fileId: number, boughtFromVendorId: number) => api.post<RateSheetRead>(`${base}/${hotelId}/rate-sheet`, { fileId, boughtFromVendorId }, { timeout: 180_000 }),
  get: (contractId: number) => api.get<RateSheet>(`${base}/contracts/${contractId}/rate-sheet`),
  answer: (contractId: number, itemId: number, answer: string | null) => api.post<RateSheet>(`${base}/contracts/${contractId}/rate-sheet/items/${itemId}`, { answer }),
};

export type ReviewKind = 'Hotel' | 'Contract';
export type ReviewInput = 'text' | 'number' | 'textarea' | 'yesno' | 'none';
export interface HotelReviewItem { key: string; label: string; value?: string | null; input: ReviewInput; ticked: boolean; editedFrom?: string | null; fileId?: number | null }
export interface HotelReviewRateRow { key: string; mealPlan: string; values: Record<string, number | null>; editedFrom: Record<string, string>; ticked: boolean }
export interface HotelReviewSection { key: string; title: string; tab: string; items: HotelReviewItem[]; rows?: HotelReviewRateRow[] | null; market?: string | null; season?: string | null }
export interface HotelReview {
  kind: ReviewKind; id: number; title: string; subtitle: string; senderName?: string | null; senderCode?: string | null; status: string; tabs: string[];
  sections: HotelReviewSection[]; ticked: number; total: number; edits: { key: string; label: string; old?: string | null; new?: string | null }[];
  missing: string[]; hotelId: number; contractId?: number | null;
}
export interface HotelApproval { kind: ReviewKind; id: number; hotelId: number; title: string; subtitle: string; submittedAt?: string | null; fromPortal: boolean; ticked: number; total: number }

export const HOTEL_APPROVALS_KEY = 'hotel-approvals';
export const hotelReviewApi = {
  approvals: () => api.get<HotelApproval[]>(`${base}/approvals`),
  get: (kind: ReviewKind, id: number) => api.get<HotelReview>(`${base}/review/${kind.toLowerCase()}/${id}`),
  save: (kind: ReviewKind, id: number, ticked: string[], edits: { key: string; value: string | null }[]) =>
    api.put<HotelReview>(`${base}/review/${kind.toLowerCase()}/${id}`, { ticked, edits }),
  decide: (kind: ReviewKind, id: number, decision: 'Approve' | 'Correction' | 'Reject', reason?: string | null) =>
    api.post<string>(`${base}/review/${kind.toLowerCase()}/${id}/decision`, { decision, reason }),
};
